import type { OfflineConfig } from '@tanstack/offline-transactions'
import { NonRetriableError } from '@tanstack/offline-transactions'
import { startOfflineExecutor } from '@tanstack/offline-transactions/react-native'
import type { TimeEntries } from '@/types/pbSchema'
import { AsyncStorageAdapter } from './async-storage'
import { captureException } from './errors'
import { log } from './logger'
import { pb, stores } from './pocketbase'

const storage = new AsyncStorageAdapter('jobtimey/')

const collections = {
    time_entries: stores.time_entries,
} as unknown as OfflineConfig['collections']

interface PendingMutation {
    type: string
    modified: unknown
    changes: unknown
}

interface SyncParams {
    transaction: { mutations: PendingMutation[] }
    idempotencyKey: string
}

// Tri-state so the caller can distinguish "confirmed no duplicate" from
// "couldn't check". A network failure here must NOT be read as "no duplicate" —
// that would let a transient error discard a real clock-in.
type DuplicateCheck = 'duplicate' | 'no-duplicate' | 'unknown'

async function checkExistingActiveEntry(userId: string, orgId: string): Promise<DuplicateCheck> {
    try {
        const entries = await pb.collection('time_entries').getList(1, 1, {
            filter: `user = "${userId}" && org = "${orgId}" && status = "active" && clock_out = ""`,
        })
        return entries.totalItems > 0 ? 'duplicate' : 'no-duplicate'
    } catch (error) {
        log.warn('syncTimeEntries: duplicate-check query failed, treating as unknown', { error })
        return 'unknown'
    }
}

async function removeStaleInsert(recordId: string) {
    try {
        const globalKey = `KEY::time_entries/${recordId}`
        const outbox = await offlineExecutor.peekOutbox()
        for (const tx of outbox) {
            const hasInsertForRecord =
                tx.keys.includes(globalKey) && tx.mutations.some(m => m.type === 'insert')
            if (hasInsertForRecord) {
                log.info('syncTimeEntries: removing stale insert transaction', {
                    transactionId: tx.id,
                    recordId,
                })
                await offlineExecutor.removeFromOutbox(tx.id)
            }
        }
    } catch (error) {
        log.warn('syncTimeEntries: failed to remove stale insert', {
            recordId,
            error,
        })
    }
}

async function syncInsert(record: TimeEntries, idempotencyKey: string) {
    log.info('syncTimeEntries: creating record', { id: record.id, idempotencyKey })
    try {
        const serverRecord = await pb.collection('time_entries').create(record, {
            headers: { 'Idempotency-Key': idempotencyKey },
        })
        stores.time_entries.utils?.writeUpsert(serverRecord)
        log.info('syncTimeEntries: create succeeded', { id: record.id })
    } catch (error) {
        const isActiveNonBreak = record.status === 'active' && record.entry_type !== 'break'
        const is400 = (error as { status?: number })?.status === 400

        if (isActiveNonBreak && is400) {
            const check = await checkExistingActiveEntry(record.user, record.org)

            // A confirmed duplicate means the unique index already holds the
            // authoritative entry (e.g. the OS geofence ENTER beat this offline
            // insert). Drop our optimistic copy and converge on the server's.
            if (check === 'duplicate') {
                log.info(
                    'syncTimeEntries: duplicate active entry blocked by unique index, discarding',
                    { id: record.id }
                )
                stores.time_entries.utils?.writeDelete(record.id)
                return
            }

            // We got a 400 but couldn't verify whether a duplicate actually
            // exists (the check query failed). Fail OPEN: rethrow so the outbox
            // retries later rather than silently discarding a possibly-real
            // clock-in. The original 400 is non-retriable, so wrap as a generic
            // error to keep it in the outbox.
            if (check === 'unknown') {
                log.warn(
                    'syncTimeEntries: 400 on active insert but duplicate unconfirmed, will retry',
                    { id: record.id }
                )
                throw new Error('duplicate check unavailable; retrying insert')
            }
        }

        throw error
    }
}

async function syncUpdate(
    record: TimeEntries,
    changes: Partial<TimeEntries>,
    idempotencyKey: string
) {
    log.info('syncTimeEntries: updating record', {
        id: record.id,
        changeKeys: Object.keys(changes),
        idempotencyKey,
    })
    try {
        const serverRecord = await pb.collection('time_entries').update(record.id, changes, {
            headers: { 'Idempotency-Key': idempotencyKey },
        })
        stores.time_entries.utils?.writeUpsert(serverRecord)
    } catch (updateError) {
        if ((updateError as { status?: number })?.status === 404) {
            log.info('syncTimeEntries: record not found, creating instead', {
                id: record.id,
                idempotencyKey,
            })
            const serverRecord = await pb.collection('time_entries').create(record, {
                headers: { 'Idempotency-Key': idempotencyKey },
            })
            stores.time_entries.utils?.writeUpsert(serverRecord)
            await removeStaleInsert(record.id)
        } else {
            throw updateError
        }
    }
    log.info('syncTimeEntries: update succeeded', { id: record.id })
}

async function syncDelete(record: TimeEntries) {
    log.info('syncTimeEntries: deleting record', { id: record.id })
    await pb.collection('time_entries').delete(record.id)
    log.info('syncTimeEntries: delete succeeded', { id: record.id })
}

async function syncMutation(mutation: PendingMutation, idempotencyKey: string) {
    const record = mutation.modified as unknown as TimeEntries
    try {
        if (mutation.type === 'insert') {
            await syncInsert(record, idempotencyKey)
        } else if (mutation.type === 'update') {
            await syncUpdate(record, mutation.changes as Partial<TimeEntries>, idempotencyKey)
        } else if (mutation.type === 'delete') {
            await syncDelete(record)
        }
    } catch (error) {
        const status = (error as { status?: number })?.status
        if (status === 400 || status === 403 || status === 404) {
            captureException('Offline sync failed permanently', error, {
                mutationType: mutation.type,
                recordId: record.id,
                idempotencyKey,
            })
            throw new NonRetriableError(`Sync failed with status ${status}`)
        }
        log.warn(
            `syncTimeEntries: operation failed (${mutation.type} ${record.id}, status=${status})`
        )
        throw error
    }
}

export async function syncTimeEntries({ transaction, idempotencyKey }: SyncParams) {
    log.info('syncTimeEntries: starting', {
        idempotencyKey,
        mutationCount: transaction.mutations.length,
        mutations: transaction.mutations.map(m => ({
            type: m.type,
            id: (m.modified as { id?: string })?.id,
        })),
    })

    for (const mutation of transaction.mutations) {
        await syncMutation(mutation, idempotencyKey)
    }

    log.info('syncTimeEntries: completed', {
        idempotencyKey,
        mutationCount: transaction.mutations.length,
    })
}

export const offlineExecutor = startOfflineExecutor({
    collections,
    storage,
    maxConcurrency: 1,
    mutationFns: {
        syncTimeEntries,
    },
})

// Expose for E2E tests to trigger outbox flush after simulated offline→online transitions
if (typeof window !== 'undefined') {
    ;(window as unknown as Record<string, unknown>).__offlineExecutor = offlineExecutor
}
